import React, { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./form-contact.scss";

type ContactFormValues = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type ContactFormErrors = Partial<Record<keyof ContactFormValues, string>>;

const initialValues: ContactFormValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export function FormContact() {
  const { t } = useTranslation();
  const [values, setValues] = useState<ContactFormValues>(initialValues);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const fieldIds = useMemo(
    () => ({
      name: "form-contact-name",
      email: "form-contact-email",
      subject: "form-contact-subject",
      message: "form-contact-message",
    }),
    []
  );

  const validate = (formValues: ContactFormValues) => {
    const nextErrors: ContactFormErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formValues.name.trim()) {
      nextErrors.name = t("formContactRequiredError");
    }

    if (!formValues.email.trim()) {
      nextErrors.email = t("formContactRequiredError");
    } else if (!emailPattern.test(formValues.email.trim())) {
      nextErrors.email = t("formContactEmailError");
    }

    if (!formValues.subject.trim()) {
      nextErrors.subject = t("formContactRequiredError");
    }

    if (!formValues.message.trim()) {
      nextErrors.message = t("formContactRequiredError");
    }

    return nextErrors;
  };

  const handleChange = (
    field: keyof ContactFormValues,
    value: string
  ) => {
    const nextValues = { ...values, [field]: value };
    setValues(nextValues);
    setSubmitted(false);

    if (errors[field]) {
      setErrors(validate(nextValues));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validate(values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitted(false);
      return;
    }

    setSubmitted(true);
    setValues(initialValues);
  };

  return (
    <section className="form-contact-section">
      <div className="form-contact-copy">
        <h1>{t("formContactTitle")}</h1>
        <p>{t("formContactDescription")}</p>
        <span aria-hidden="true" />
      </div>

      <form className="form-contact-card" onSubmit={handleSubmit} noValidate>
        <div className="form-contact-field">
          <label htmlFor={fieldIds.name}>{t("formContactNameLabel")}</label>
          <input
            id={fieldIds.name}
            type="text"
            value={values.name}
            placeholder={t("formContactNamePlaceholder")}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${fieldIds.name}-error` : undefined}
            onChange={(event) => handleChange("name", event.target.value)}
          />
          {errors.name && (
            <small id={`${fieldIds.name}-error`}>{errors.name}</small>
          )}
        </div>

        <div className="form-contact-field">
          <label htmlFor={fieldIds.email}>{t("formContactEmailLabel")}</label>
          <input
            id={fieldIds.email}
            type="email"
            value={values.email}
            placeholder={t("formContactEmailPlaceholder")}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={
              errors.email ? `${fieldIds.email}-error` : undefined
            }
            onChange={(event) => handleChange("email", event.target.value)}
          />
          {errors.email && (
            <small id={`${fieldIds.email}-error`}>{errors.email}</small>
          )}
        </div>

        <div className="form-contact-field">
          <label htmlFor={fieldIds.subject}>
            {t("formContactSubjectLabel")}
          </label>
          <input
            id={fieldIds.subject}
            type="text"
            value={values.subject}
            placeholder={t("formContactSubjectPlaceholder")}
            aria-invalid={Boolean(errors.subject)}
            aria-describedby={
              errors.subject ? `${fieldIds.subject}-error` : undefined
            }
            onChange={(event) => handleChange("subject", event.target.value)}
          />
          {errors.subject && (
            <small id={`${fieldIds.subject}-error`}>{errors.subject}</small>
          )}
        </div>

        <div className="form-contact-field">
          <label htmlFor={fieldIds.message}>
            {t("formContactMessageLabel")}
          </label>
          <textarea
            id={fieldIds.message}
            value={values.message}
            placeholder={t("formContactMessagePlaceholder")}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={
              errors.message ? `${fieldIds.message}-error` : undefined
            }
            onChange={(event) => handleChange("message", event.target.value)}
          />
          {errors.message && (
            <small id={`${fieldIds.message}-error`}>{errors.message}</small>
          )}
        </div>

        <button
          type="submit"
          className={submitted ? "form-contact-submit-sent" : ""}
        >
          {submitted ? t("formContactSent") : t("formContactSubmit")}
        </button>
      </form>
    </section>
  );
}
